/**
 * Settings Filters - AJAX Field Handler
 * Handles automatic saving of filter settings when fields are changed
 * 
 * @author InterPals Development Team
 * @version 1.0.0
 */

import { createTimeoutSignal } from '../modules/timeout-signal.js';

(function() {
    'use strict';

    /**
     * Translate through the public JS catalog (window.trans from i18n.js).
     * @param {string} domain - PO domain
     * @param {string} key - Message id
     * @param {Object<string, string|number>} [params] - Placeholder values
     * @returns {string}
     */
    function trans(domain, key, params) {
        return typeof window.trans === 'function' ? window.trans(domain, key, params) : key;
    }

    // Constants
    const FEEDBACK_DURATION = {
        SUCCESS: 1200,
        ERROR: 2500
    };

    const REQUEST_TIMEOUT = 10000; // 10 seconds
    const DEBOUNCE_DELAY = 500; // Shared by the age slider and the goal chips

    const CSS_CLASSES = {
        HIDDEN: 'hidden',
        OPACITY_70: 'opacity-70',
        SUCCESS_FEEDBACK: 'transition-all duration-300 bg-green-50',
        ERROR_FEEDBACK: 'transition-all duration-300 bg-red-50',
        CONTINENT_CHIP: 'continent-chip',
        GOAL_CHIP: 'goal-chip',
        COUNTRY_TAG: 'country-tag',
        DISABLED: 'disabled',
        POINTER_EVENTS_NONE: 'pointer-events-none'
    };
    
    // Unified endpoint for all settings field updates
    const FIELD_UPDATE_URL = '/app/settings/field/update';

    const SELECTORS = {
        CSRF_TOKEN_META: 'meta[name="csrf_token"]',
        CSRF_TOKEN_INPUT: 'input[name="csrf_token"]',
        AGE_SLIDER_MIN: 'input[name="contact_rules_age1"]',
        AGE_SLIDER_MAX: 'input[name="contact_rules_age2"]',
        SEX_SELECT: '.inline-select-container[data-name="contact_rules_sex"]',
        CONTINENT_CHIPS_CONTAINER: '[data-field="blocked_continents"]',
        GOAL_CHIPS_CONTAINER: '[data-field="lfor_blocked"]',
        COUNTRY_SELECTOR: '#blocked-countries-selector'
    };

    let ageSliderTimeout = null;

    /**
     * Get CSRF token from meta tag or hidden input
     * @returns {string|null} CSRF token value or null if not found
     */
    function getCsrfToken() {
        const csrfTokenMeta = document.querySelector(SELECTORS.CSRF_TOKEN_META);
        const csrfTokenInput = document.querySelector(SELECTORS.CSRF_TOKEN_INPUT);

        if (csrfTokenMeta) {
            return csrfTokenMeta.getAttribute('content');
        } else if (csrfTokenInput) {
            return csrfTokenInput.value;
        }
        
        return null;
    }

    /**
     * Apply visual feedback to an element
     * @param {HTMLElement} element - The element
     * @param {string} type - 'success' or 'error'
     * @param {number} duration - Duration in milliseconds
     */
    function applyVisualFeedback(element, type, duration) {
        if (!element) return;

        // Remove opacity immediately
        element.classList.remove(CSS_CLASSES.OPACITY_70);

        if (type === 'success') {
            // Show a floating checkmark indicator
            showSaveIndicator(element, true);
        } else if (type === 'error') {
            // Show error state briefly
            element.classList.add('bg-red-50', 'dark:bg-red-900/30');
            setTimeout(function() {
                element.classList.remove('bg-red-50', 'dark:bg-red-900/30');
            }, duration);
        }
    }

    /**
     * Show a save indicator below the slider
     * @param {HTMLElement} element - The element to attach to
     * @param {boolean} success - Whether it was successful
     */
    function showSaveIndicator(element, success) {
        // Remove any existing indicator
        const existingIndicator = element.querySelector('.save-indicator');
        if (existingIndicator) {
            existingIndicator.remove();
        }

        // Create the indicator
        const indicator = document.createElement('div');
        indicator.className = 'save-indicator';
        indicator.innerHTML = success
            ? '<svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path></svg>'
            : '<svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
        indicator.appendChild(document.createTextNode(success ? trans('common', 'saved') : trans('common', 'error')));

        // Style it - position below the slider, left aligned
        Object.assign(indicator.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '8px',
            color: success ? '#10b981' : '#ef4444',
            fontSize: '13px',
            fontWeight: '500',
            animation: 'fadeIn 0.2s ease-out'
        });

        // Add animation keyframes if not already present
        if (!document.getElementById('save-indicator-styles')) {
            const style = document.createElement('style');
            style.id = 'save-indicator-styles';
            style.textContent = `
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fadeOut {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        element.appendChild(indicator);

        // Remove after delay
        setTimeout(function() {
            indicator.style.animation = 'fadeOut 0.3s ease-out forwards';
            setTimeout(function() {
                indicator.remove();
            }, 300);
        }, 1200);
    }

    /**
     * Send field update to server
     * @param {string} fieldName - Field name
     * @param {string} fieldValue - Field value
     * @param {HTMLElement} container - Container element for visual feedback
     * @param {Function} [onError] - Error callback
     * @param {Function} [onSuccess] - Success callback, receives the response payload
     */
    function sendFieldUpdate(fieldName, fieldValue, container, onError, onSuccess) {
        // Add visual loading state
        if (container) {
            container.classList.add(CSS_CLASSES.OPACITY_70);
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('field', fieldName);
        formData.append('value', fieldValue);

        // Add CSRF token
        const tokenValue = getCsrfToken();
        if (tokenValue) {
            formData.append('csrf_token', tokenValue);
        }

        // Abort a request that never settles so a caller's error path always
        // runs. The goal-chip saver serializes on an in-flight flag, and a
        // hung request would otherwise wedge every later save on the page.
        const timeout = createTimeoutSignal(REQUEST_TIMEOUT);

        // Make AJAX request
        fetch(FIELD_UPDATE_URL, {
            method: 'POST',
            body: formData,
            signal: timeout.signal,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(function(response) {
            timeout.clear();
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(function(data) {
            // Always remove loading state first
            if (container) {
                container.classList.remove(CSS_CLASSES.OPACITY_70);
            }
            
            if (data.success) {
                // Show success feedback
                applyVisualFeedback(container, 'success', FEEDBACK_DURATION.SUCCESS);
                if (onSuccess) {
                    // Deferred out of this promise chain so a throw inside the
                    // success callback cannot fall through to the .catch below
                    // and run the error callback for a save that succeeded.
                    queueMicrotask(function() {
                        onSuccess(data);
                    });
                }
            } else {
                const serverError = new Error(data.error_msg || 'Unknown error occurred');
                // Mark messages that came from the server so error handlers can
                // show them to the user. Server messages are localized, unlike
                // the hardcoded fallbacks in this file.
                if (data.error_msg) {
                    serverError.serverMessage = data.error_msg;
                }
                throw serverError;
            }
        })
        .catch(function(error) {
            timeout.clear();
            // Always remove loading state first
            if (container) {
                container.classList.remove(CSS_CLASSES.OPACITY_70);
            }

            // Show error feedback
            applyVisualFeedback(container, 'error', FEEDBACK_DURATION.ERROR);
            if (onError) {
                onError(error);
            }
        });
    }

    /**
     * Handle age slider changes
     */
    function handleAgeSliderChange(values) {
        // Clear existing timeout
        if (ageSliderTimeout) {
            clearTimeout(ageSliderTimeout);
        }

        // Debounce the save
        ageSliderTimeout = setTimeout(function() {
            let minAge, maxAge;

            if (values && values.length === 2) {
                // jQuery UI slider provides values array
                minAge = values[0];
                maxAge = values[1];
            } else {
                // Fallback to reading from hidden inputs
                const minSlider = document.querySelector(SELECTORS.AGE_SLIDER_MIN);
                const maxSlider = document.querySelector(SELECTORS.AGE_SLIDER_MAX);
                if (!minSlider || !maxSlider) return;
                minAge = minSlider.value;
                maxAge = maxSlider.value;
            }

            // Convert 65 to 110 for storage (slider displays "65+" but stores 110)
            if (parseInt(maxAge) === 65) {
                maxAge = 110;
            }

            const ageRange = minAge + ',' + maxAge;
            const container = document.querySelector('.double-range-slider-container');
            if (container) {
                // Use the slider container itself for more specific loading state
                sendFieldUpdate('contact_rules_age', ageRange, container);
            }
        }, DEBOUNCE_DELAY);
    }

    /**
     * Initialize age sliders
     */
    function initAgeSliders() {
        // Wait for jQuery UI to be ready and then watch for changes to the hidden inputs
        setTimeout(function() {
            const minInput = document.querySelector(SELECTORS.AGE_SLIDER_MIN);
            const maxInput = document.querySelector(SELECTORS.AGE_SLIDER_MAX);
            
            if (!minInput || !maxInput) {
                // Age sliders only exist on settings/filters page - silently skip on other pages
                return;
            }
            
            // Watch for value changes on the hidden inputs
            // The jQuery UI slider updates these inputs when sliding
            let lastMinValue = minInput.value;
            let lastMaxValue = maxInput.value;
            
            // Use MutationObserver to detect value changes
            const observer = new MutationObserver(function(mutations) {
                const currentMinValue = minInput.value;
                const currentMaxValue = maxInput.value;
                
                if (currentMinValue !== lastMinValue || currentMaxValue !== lastMaxValue) {
                    lastMinValue = currentMinValue;
                    lastMaxValue = currentMaxValue;
                    handleAgeSliderChange();
                }
            });
            
            // Observe changes to the value attribute
            observer.observe(minInput, { attributes: true, attributeFilter: ['value'] });
            observer.observe(maxInput, { attributes: true, attributeFilter: ['value'] });
            
            // Also listen for the jQuery UI slider if available
            const sliderElement = document.getElementById('contact_rules_age1_contact_rules_age2_slider');
            if (sliderElement && typeof $ !== 'undefined' && $.fn.slider) {
                $(sliderElement).on('slidestop', function(event, ui) {
                    // Force a check when sliding stops
                    handleAgeSliderChange(ui.values);
                });
            }
            
            // Fallback: poll for changes every 500ms while user might be interacting
            let pollInterval = null;
            const startPolling = function() {
                if (!pollInterval) {
                    pollInterval = setInterval(function() {
                        const currentMinValue = minInput.value;
                        const currentMaxValue = maxInput.value;
                        
                        if (currentMinValue !== lastMinValue || currentMaxValue !== lastMaxValue) {
                            lastMinValue = currentMinValue;
                            lastMaxValue = currentMaxValue;
                            handleAgeSliderChange();
                        }
                    }, 500);
                }
            };
            
            const stopPolling = function() {
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            };
            
            // Start polling on mousedown/touchstart on the slider container
            const sliderContainer = document.querySelector('.double-range-slider-container');
            if (sliderContainer) {
                sliderContainer.addEventListener('mousedown', startPolling);
                sliderContainer.addEventListener('touchstart', startPolling);
                
                // Stop polling on mouseup/touchend
                document.addEventListener('mouseup', stopPolling);
                document.addEventListener('touchend', stopPolling);
            }
        }, 500); // Give jQuery UI time to initialize
    }

    /**
     * Initialize contact rules sex field
     */
    function initContactRulesSex() {
        // Listen for inline select change events
        const sexContainer = document.querySelector(SELECTORS.SEX_SELECT);
        if (sexContainer) {
            sexContainer.addEventListener('inlineSelectChange', function(event) {
                const hiddenInput = sexContainer.querySelector('input[type="hidden"]');
                if (hiddenInput) {
                    // Use the sex container itself for more specific loading state
                    sendFieldUpdate('contact_rules_sex', hiddenInput.value, sexContainer);
                }
            });
        }
    }

    /**
     * Get current continent values
     */
    function getCurrentContinents() {
        const checkboxes = document.querySelectorAll('.continent-chip input[type="checkbox"]:checked');
        const continents = [];
        checkboxes.forEach(function(checkbox) {
            if (checkbox.value) {
                continents.push(checkbox.value);
            }
        });
        return continents.join(',');
    }

    /**
     * Initialize continent chips
     */
    function initContinentChips() {
        // Use a slight delay to ensure multi-select.js has initialized first
        setTimeout(function() {
            const chips = document.querySelectorAll('.' + CSS_CLASSES.CONTINENT_CHIP);
            chips.forEach(function(chip) {
                // The chips are labels, so we need to handle the checkbox change instead
                const checkbox = chip.querySelector('input[type="checkbox"]');
                if (!checkbox) return;
                
                // Listen for checkbox change events
                checkbox.addEventListener('change', function() {
                    // Get the continent chips wrapper specifically for loading state
                    const chipsWrapper = chip.closest('.continent-chips-wrapper');
                    if (!chipsWrapper) {
                        // Fallback to the data-field container
                        const container = chip.closest('[data-field="blocked_continents"]');
                        if (container) {
                            sendFieldUpdate('blocked_continents', getCurrentContinents(), container);
                        }
                    } else {
                        // Send update after a small delay to ensure the checkbox state is updated
                        setTimeout(function() {
                            const continents = getCurrentContinents();
                            sendFieldUpdate('blocked_continents', continents, chipsWrapper);
                        }, 10);
                    }
                });
            });
        }, 100);
    }

    // Class sets mirror the server-rendered chip state in
    // components/forms.twig (filter_goals_row). Keep the two in sync.
    const GOAL_CHIP_ACTIVE_CLASSES = [
        'border-rose-400', 'bg-rose-50/50',
        'dark:border-rose-500', 'dark:bg-rose-400/10'
    ];
    const GOAL_CHIP_INACTIVE_CLASSES = [
        'border-gray-200', 'hover:border-gray-300', 'hover:bg-gray-50',
        'dark:border-gray-700', 'dark:hover:border-gray-600', 'dark:hover:bg-gray-800/50'
    ];

    let goalChipsTimeout = null;

    /**
     * Get current blocked goal values
     */
    function getCurrentGoals(container) {
        const checkboxes = container.querySelectorAll(
            '.' + CSS_CLASSES.GOAL_CHIP + ' input[type="checkbox"]:checked'
        );
        const goals = [];
        checkboxes.forEach(function(checkbox) {
            if (checkbox.value) {
                goals.push(checkbox.value);
            }
        });
        return goals.join(',');
    }

    /**
     * Apply the active/inactive class set to a goal chip
     */
    function syncGoalChipClasses(chip, isActive) {
        const addClasses = isActive ? GOAL_CHIP_ACTIVE_CLASSES : GOAL_CHIP_INACTIVE_CLASSES;
        const removeClasses = isActive ? GOAL_CHIP_INACTIVE_CLASSES : GOAL_CHIP_ACTIVE_CLASSES;
        removeClasses.forEach(function(cls) { chip.classList.remove(cls); });
        addClasses.forEach(function(cls) { chip.classList.add(cls); });
    }

    /**
     * Initialize blocked-goal chips (premium contact intent filter).
     * Saves are debounced so rapid toggles produce one request with the
     * final state, and a rejected save rolls the chips back to the last
     * state the server accepted.
     */
    function initGoalChips() {
        const container = document.querySelector(SELECTORS.GOAL_CHIPS_CONTAINER);
        if (!container) {
            return;
        }
        const wrapper = container.querySelector('.goal-chips-wrapper');
        // The chip set the server is known to hold, used as the rollback
        // target when a save is rejected.
        let acceptedGoals = getCurrentGoals(container);
        let saveInFlight = false;
        let resaveQueued = false;

        function restoreGoals(savedCsv) {
            const saved = savedCsv === '' ? [] : savedCsv.split(',');
            container.querySelectorAll('.' + CSS_CLASSES.GOAL_CHIP).forEach(function(chip) {
                const checkbox = chip.querySelector('input[type="checkbox"]');
                if (!checkbox) {
                    return;
                }
                checkbox.checked = saved.indexOf(checkbox.value) !== -1;
                syncGoalChipClasses(chip, checkbox.checked);
            });
        }

        function runGoalSave() {
            const goals = getCurrentGoals(container);
            if (goals === acceptedGoals) {
                return;
            }

            const rollbackGoals = acceptedGoals;
            saveInFlight = true;

            // A toggle made while the request was open is newer intent, so it
            // is saved next and the response never overwrites the chips.
            function drainQueue() {
                saveInFlight = false;
                if (!resaveQueued) {
                    return;
                }
                resaveQueued = false;
                // The queued save reads the current DOM state, so a debounce
                // timer still pending would only re-send what is dispatched
                // here. Cancel it instead of letting it fire redundantly.
                if (goalChipsTimeout) {
                    clearTimeout(goalChipsTimeout);
                    goalChipsTimeout = null;
                }
                runGoalSave();
            }

            sendFieldUpdate('lfor_blocked', goals, wrapper, function(error) {
                // Roll back only while the chips still show what was sent. A
                // toggle made since then is newer intent, whether it has
                // reached the queue yet or is still inside the debounce.
                if (getCurrentGoals(container) === goals) {
                    acceptedGoals = rollbackGoals;
                    restoreGoals(rollbackGoals);
                }
                // Server rejections carry a localized message (for example the
                // premium-required error a lapsed member gets when re-adding a
                // goal). Network failures have no serverMessage and keep the
                // generic flash only.
                if (error && error.serverMessage) {
                    showInlineError(container, error.serverMessage);
                }
                drainQueue();
            }, function() {
                // The server holds this set now, whether or not a newer edit
                // is already on its way. Clear any error left by an earlier
                // rejected save so it cannot linger next to a success state.
                removeInlineErrors(container);
                acceptedGoals = goals;
                drainQueue();
            });
        }

        function scheduleGoalSave() {
            if (goalChipsTimeout) {
                clearTimeout(goalChipsTimeout);
            }
            goalChipsTimeout = setTimeout(function() {
                // One request at a time, so the server applies saves in the
                // order the user made them.
                if (saveInFlight) {
                    resaveQueued = true;
                    return;
                }
                runGoalSave();
            }, DEBOUNCE_DELAY);
        }

        container.querySelectorAll('.' + CSS_CLASSES.GOAL_CHIP).forEach(function(chip) {
            const checkbox = chip.querySelector('input[type="checkbox"]');
            if (!checkbox) {
                return;
            }
            checkbox.addEventListener('change', function() {
                syncGoalChipClasses(chip, checkbox.checked);
                scheduleGoalSave();
            });
        });
    }

    /**
     * Get current country values
     */
    function getCurrentCountries() {
        // Support both legacy multi-select (.country-tag) and Alpine countryChips (hidden inputs)
        const hiddenInputs = document.querySelectorAll('input[type="hidden"][name="blocked_countries[]"]');
        if (hiddenInputs.length > 0) {
            const countries = [];
            hiddenInputs.forEach(function(input) {
                if (input.value) {
                    countries.push(input.value);
                }
            });
            return countries.join(',');
        }
        // Fallback to legacy selector
        const countryTags = document.querySelectorAll('.' + CSS_CLASSES.COUNTRY_TAG);
        const countries = [];
        countryTags.forEach(function(tag) {
            const countryCode = tag.getAttribute('data-country');
            if (countryCode) {
                countries.push(countryCode);
            }
        });
        return countries.join(',');
    }

    /**
     * Initialize country selector
     */
    function initCountrySelector() {
        function handleCountryChange() {
            let container = document.querySelector(SELECTORS.COUNTRY_SELECTOR);
            if (!container) {
                container = document.getElementById('blocked-countries-row');
            }
            if (container) {
                const chipsContainer = container.querySelector('.selected-countries-chips');
                const targetContainer = chipsContainer || container;
                // Defer to let Alpine update the DOM (hidden inputs via x-for)
                setTimeout(function() {
                    const countries = getCurrentCountries();
                    sendFieldUpdate('blocked_countries', countries, targetContainer);
                }, 50);
            }
        }

        document.addEventListener('countryAdded', handleCountryChange);
        document.addEventListener('countryRemoved', handleCountryChange);
    }

    /**
     * Show inline error message
     * @param {HTMLElement} container - The container element
     * @param {string} message - Error message to display
     */
    function showInlineError(container, message) {
        // Remove any existing error messages
        removeInlineErrors(container);
        
        // Create error element. The message is inserted as text, never as
        // HTML, so a server error string that ever carries user input cannot
        // become an injection sink.
        const errorDiv = document.createElement('div');
        errorDiv.className = 'inline-error text-xs text-red-600 dark:text-red-400 mt-2 flex items-center gap-1';
        /* lucide-icons: circle-alert */
        const errorIcon = document.createElement('i');
        errorIcon.setAttribute('data-lucide', 'circle-alert');
        errorIcon.className = 'w-3.5 h-3.5 flex-none';
        errorDiv.appendChild(errorIcon);
        errorDiv.appendChild(document.createTextNode(message));
        
        // Insert after the container
        container.appendChild(errorDiv);
        window.LucideIcons?.initialize(errorDiv);
        
        // Auto-remove after 5 seconds
        setTimeout(function() {
            removeInlineErrors(container);
        }, 5000);
    }
    
    /**
     * Remove inline error messages
     * @param {HTMLElement} container - The container element
     */
    function removeInlineErrors(container) {
        const errors = container.querySelectorAll('.inline-error');
        errors.forEach(function(error) {
            error.remove();
        });
    }


    /**
     * Initialize block list handlers (new chip-based system)
     */
    function initBlockList() {
        const addInput = document.getElementById('add-blocked-user-input');
        const addBtn = document.getElementById('add-blocked-user-btn');
        const chipsContainer = document.getElementById('blocked-users-chips');
        const statusDiv = document.getElementById('block-list-status');
        const errorDiv = document.getElementById('block-user-error');
        const usageText = document.getElementById('blocked-users-usage');
        
        if (!addInput || !addBtn) {
            return;
        }
        
        // Enable/disable add button based on input
        addInput.addEventListener('input', function() {
            const hasValue = this.value.trim().length > 0;
            addBtn.disabled = !hasValue;
            // Clear error when typing
            if (errorDiv) {
                errorDiv.classList.add('hidden');
                errorDiv.textContent = '';
            }
        });
        
        // Handle Enter key in input
        addInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter' && !addBtn.disabled) {
                e.preventDefault();
                handleBlockUser();
            }
        });
        
        // Handle add button click
        addBtn.addEventListener('click', handleBlockUser);
        
        // Handle unblock buttons
        document.addEventListener('click', function(e) {
            if (e.target.closest('.unblock-user-btn')) {
                const btn = e.target.closest('.unblock-user-btn');
                const username = btn.dataset.username;
                if (username) {
                    handleUnblockUser(username);
                }
            }
        });
        
        /**
         * Handle blocking a user
         */
        function handleBlockUser() {
            const username = addInput.value.trim();
            if (!username) return;
            
            // Disable controls during request
            addInput.disabled = true;
            addBtn.disabled = true;
            
            // Show loading state
            showStatus(trans('settings', 'block_user_pending'), 'info');
            
            // Prepare form data
            const formData = new FormData();
            formData.append('field', 'block_user');
            formData.append('username', username);
            
            // Add CSRF token
            const tokenValue = getCsrfToken();
            if (tokenValue) {
                formData.append('csrf_token', tokenValue);
            }
            
            // Make AJAX request
            fetch(FIELD_UPDATE_URL, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    // Add chip to UI
                    addUserChip(data.user.username);
                    
                    // Clear input
                    addInput.value = '';
                    
                    // Update count
                    updateBlockedCount(1);
                    
                    // Show success
                    showStatus(trans('settings', 'block_user_success'), 'success');
                } else {
                    // Show error
                    showError(data.error_msg || trans('settings', 'block_user_failed'));
                }
            })
            .catch(error => {
                showError(trans('common', 'error_network'));
            })
            .finally(() => {
                // Re-enable controls
                addInput.disabled = false;
                addBtn.disabled = false;
                addInput.focus();
            });
        }
        
        /**
         * Handle unblocking a user
         */
        function handleUnblockUser(username) {
            const chip = document.querySelector(`.blocked-user-chip[data-username="${username}"]`);
            if (!chip) return;
            
            // Show loading state on chip
            chip.classList.add('opacity-50');
            const btn = chip.querySelector('.unblock-user-btn');
            if (btn) btn.disabled = true;
            
            showStatus(trans('settings', 'unblock_user_pending'), 'info');
            
            // Prepare form data
            const formData = new FormData();
            formData.append('field', 'unblock_user');
            formData.append('username', username);
            
            // Add CSRF token
            const tokenValue = getCsrfToken();
            if (tokenValue) {
                formData.append('csrf_token', tokenValue);
            }
            
            // Make AJAX request
            fetch(FIELD_UPDATE_URL, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    // Remove chip with animation
                    chip.style.transition = 'all 0.3s ease-out';
                    chip.style.transform = 'scale(0.8)';
                    chip.style.opacity = '0';
                    
                    setTimeout(() => {
                        chip.remove();
                        checkEmptyState();
                    }, 300);
                    
                    // Update count
                    updateBlockedCount(-1);
                    
                    // Show success
                    showStatus(trans('settings', 'unblock_user_success'), 'success');
                } else {
                    // Restore chip state
                    chip.classList.remove('opacity-50');
                    if (btn) btn.disabled = false;
                    
                    showError(data.error_msg || trans('settings', 'unblock_user_failed'));
                }
            })
            .catch(error => {
                // Restore chip state
                chip.classList.remove('opacity-50');
                if (btn) btn.disabled = false;
                
                showError(trans('common', 'error_network'));
            });
        }
        
        /**
         * Add a user chip to the container
         */
        function addUserChip(username) {
            const emptyMsg = document.getElementById('blocked-users-empty');
            if (emptyMsg) {
                emptyMsg.remove();
            }
            
            // Create chip element
            const chip = document.createElement('div');
            chip.className = 'blocked-user-chip inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm group hover:border-red-300 transition-colors';
            chip.dataset.username = username;
            // Escape username for HTML
            const escapeHtml = (str) => {
                const div = document.createElement('div');
                div.textContent = str;
                return div.innerHTML;
            };
            
            chip.innerHTML = `
                <a href="/${escapeHtml(username)}" class="text-gray-700 hover:text-sky-600 hover:underline">${escapeHtml(username)}</a>
                <button type="button"
                        class="unblock-user-btn text-gray-400 hover:text-red-500 transition-colors"
                        data-username="${escapeHtml(username)}"
                        aria-label="${escapeHtml(trans('profile', 'unblock_user_aria', { username: username }))}">
                    <i data-lucide="x" class="w-3.5 h-3.5"></i>
                </button>
            `;
            window.LucideIcons?.initialize(chip);
            
            // Add with animation
            chip.style.opacity = '0';
            chip.style.transform = 'scale(0.8)';
            chipsContainer.appendChild(chip);
            
            // Trigger animation
            setTimeout(() => {
                chip.style.transition = 'all 0.3s ease-out';
                chip.style.opacity = '1';
                chip.style.transform = 'scale(1)';
            }, 10);
        }
        
        /**
         * Check if chips container is empty and show message
         */
        function checkEmptyState() {
            if (chipsContainer.children.length > 0) {
                return;
            }
            // Same markup as the empty state in forms.twig filter_blocked_users_row.
            const empty = document.createElement('div');
            empty.id = 'blocked-users-empty';
            empty.className = 'flex items-center justify-center w-full py-2';
            empty.innerHTML = '<div class="flex flex-col items-center gap-1 text-gray-400">'
                + '<i data-lucide="user-x" class="w-5 h-5"></i><span class="text-sm"></span></div>';
            empty.querySelector('span').textContent = trans('settings', 'no_users_blocked');
            chipsContainer.appendChild(empty);
            window.LucideIcons?.initialize(empty);
        }
        
        /**
         * Update blocked users count
         */
        function updateBlockedCount(delta) {
            if (!usageText) {
                return;
            }
            const newCount = Math.max(0, (parseInt(usageText.dataset.count, 10) || 0) + delta);
            usageText.dataset.count = String(newCount);
            usageText.textContent = (usageText.dataset.template || '').split('%count%').join(String(newCount));
        }
        
        /**
         * Show status message
         */
        function showStatus(message, type = 'info') {
            if (!statusDiv) return;
            
            statusDiv.textContent = message;
            statusDiv.className = 'text-sm';
            
            if (type === 'success') {
                statusDiv.classList.add('text-green-600');
            } else if (type === 'error') {
                statusDiv.classList.add('text-red-600');
            } else {
                statusDiv.classList.add('text-gray-600');
            }
            
            statusDiv.classList.remove('hidden');
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
                statusDiv.classList.add('hidden');
            }, 3000);
        }
        
        /**
         * Show error message
         */
        function showError(message) {
            if (errorDiv) {
                errorDiv.textContent = message;
                errorDiv.classList.remove('hidden');
            }
            showStatus(message, 'error');
        }
    }

    /**
     * Initialize all filter fields
     */
    function initFilterFields() {
        initContactRulesSex();
        initAgeSliders();
        initContinentChips();
        initGoalChips();
        initCountrySelector();
        initBlockList();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFilterFields);
    } else {
        initFilterFields();
    }

})();